/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Gay-Berne rods for the molecular lab (todo #4): the one-ellipsoid model of a
 * liquid-crystal molecule. A rod is a bead whose type has an aspect ratio above
 * one (beadModel.ts, 'ROD'); it carries an orientation û and an angular
 * velocity ω on its Atom, and this class gives it the anisotropic pair
 * potential, the torques, the rotational half of the integrator and the
 * nematic order parameter.
 *
 * THE POTENTIAL (Gay & Berne 1981, uniaxial, GB(κ, κ′, μ, ν) with κ′ = 5,
 * μ = 2, ν = 1 and κ from the bead type):
 *   U = 4 ε(ûᵢ, ûⱼ, r̂) [R⁻¹² − R⁻⁶],  R = (r − σ(ûᵢ, ûⱼ, r̂) + σ₀) / σ₀
 *   σ = σ₀ [1 − (χ/2) S]^(−1/2),  S = (a+b)²/(1+χc) + (a−b)²/(1−χc)
 *   ε = ε₀ ε₁^ν ε₂^μ,  ε₁ = [1 − χ²c²]^(−1/2),  ε₂ = 1 − (χ′/2) S′ (S′ with χ′)
 *   a = r̂·ûᵢ, b = r̂·ûⱼ, c = ûᵢ·ûⱼ, χ = (κ²−1)/(κ²+1), χ′ = (κ′^(1/μ)−1)/(κ′^(1/μ)+1)
 * cut and shifted on R at R_c = 3.5 (the shift is applied inside the bracket,
 * so every derivative below stays consistent with the energy). Side by side
 * two rods touch at σ₀ and bind with ε₀·κ′; end to end they touch at κσ₀ and
 * bind with ε₀.
 *
 * UNLIKE RODS (Cleaver, Care, Allen & Neal 1996). For rods of lengths lᵢ, lⱼ and
 * widths dᵢ, dⱼ the shape function is
 *   σ = σ₀ [1 − (Xᵢa² + Xⱼb² − 2χ²abc) / (1 − χ²c²)]^(−1/2)
 *   σ₀ = √((dᵢ² + dⱼ²)/2),  Xᵢ = (lᵢ² − dᵢ²)/(lᵢ² + dⱼ²),  Xⱼ = (lⱼ² − dⱼ²)/(lⱼ² + dᵢ²),  χ² = XᵢXⱼ
 * which is the paper's χ and α written without α (χα² = Xᵢ, χ/α² = Xⱼ). Like
 * rods have Xᵢ = Xⱼ = χ and the equation above. The contacts are side by side
 * σ₀, end to end √((lᵢ² + lⱼ²)/2) and the tip of i on the side of j
 * √((lᵢ² + dⱼ²)/2): root-mean-square, as in every Gaussian-overlap potential, so
 * a long tip on a thin side reads longer than the hard-body (lᵢ + dⱼ)/2. The well
 * keeps χ′ because κ′ is one constant for all rods, and ε₀ mixes geometrically.
 *
 * FORCES AND TORQUES are the analytic chain rule through a, b, c and r: the
 * force on i is −∂U/∂r⃗ with r⃗ = rᵢ − rⱼ, and the torque on i is −ûᵢ × ∂U/∂ûᵢ.
 * gayBerne.test.ts checks both against numerical derivatives
 * (npm run test:gayberne), as the lab's rule for every force field requires.
 *
 * ROTATION. A rod is a linear rotor with I = m σ₀² (κ² + 1) / 20 (a uniform
 * prolate ellipsoid of length κσ₀ and width σ₀). The orientation is advanced
 * with the same predict / correct scheme as the positions (Atom.predict /
 * correct): û += (ω × û) dt + (α × û) dt²/2 then renormalized, ω += α dt, and
 * the corrector ω += (α_new − α_old) dt/2, with ω kept perpendicular to û. The
 * rotational kinetic energy ½ I ω² joins the thermometer with two degrees of
 * freedom per rod, and the velocity-rescaling thermostats scale ω with v.
 *
 * FIELD. An aligning field along ẑ of reduced strength E* adds
 * U_f = −½ ε₀ E*² (û·ẑ)² per rod (a positive dielectric anisotropy), the
 * coupling that turns a nematic's director in the Fréedericksz experiment.
 *
 * Units are the engine's: Å, eV, g/mol, fs; forces are added to Atom.force
 * already divided by the mass (as the bonded terms do, after the pair kernel's
 * own division), angular accelerations in rad/fs² use the same GF factor.
 */
import { Vector3 } from 'three';
import type { Atom } from './Atom.ts';
import { EV_CONVERTER, GF_CONVERSION_CONSTANT, VT_CONVERSION_CONSTANT } from './physicalConstants.ts';
import { ModelUtil } from './ModelUtil.ts';
import { symmetricEigenDecompositionFlat } from '../nano/symmetricEigen.ts';

export const GB_KAPPA_PRIME = 5;
export const GB_MU = 2;
export const GB_NU = 1;
/** Cutoff on R = (r − σ + σ₀)/σ₀, with the energy shifted to zero there. */
export const GB_CUTOFF_R = 3.5;
/** Extra reach of the rods' pair list (Å): it holds until pairs could have closed this gap. */
const PAIR_LIST_SKIN = 3;

const CHI_PRIME = (Math.pow(GB_KAPPA_PRIME, 1 / GB_MU) - 1) / (Math.pow(GB_KAPPA_PRIME, 1 / GB_MU) + 1);
const RC6 = Math.pow(GB_CUTOFF_R, -6);
const SHIFT = RC6 * RC6 - RC6;

export interface GbPairParams {
  sigma0: number; // Å, the rod width
  epsilon0: number; // eV, the end-to-end well
  kappa: number; // length / width (like rods; ignored when xi and xj are given)
  xi?: number; // Xᵢ of an unlike pair (gbUnlikeParams)
  xj?: number; // Xⱼ
}

/** Write the parameters of a pair of rods of any widths σ, wells ε and aspect ratios κ into p. */
const fillGbUnlikeParams = (
  p: GbPairParams,
  si: number,
  ei: number,
  ki: number,
  sj: number,
  ej: number,
  kj: number,
): GbPairParams => {
  const di2 = si * si;
  const dj2 = sj * sj;
  const li2 = ki * ki * di2;
  const lj2 = kj * kj * dj2;
  p.sigma0 = si === sj ? si : Math.sqrt((di2 + dj2) / 2);
  p.epsilon0 = ei === ej ? ei : Math.sqrt(ei * ej);
  p.kappa = 0.5 * (ki + kj);
  p.xi = (li2 - di2) / (li2 + dj2);
  p.xj = (lj2 - dj2) / (lj2 + di2);
  return p;
};

/** The parameters of a pair of rods of any widths σ, wells ε and aspect ratios κ. */
export const gbUnlikeParams = (si: number, ei: number, ki: number, sj: number, ej: number, kj: number): GbPairParams =>
  fillGbUnlikeParams({ sigma0: 0, epsilon0: 0, kappa: 0 }, si, ei, ki, sj, ej, kj);

export interface GbPairResult {
  energy: number; // eV
  force: Vector3; // on i, eV/Å (−∂U/∂r⃗, r⃗ = rᵢ − rⱼ)
  gi: Vector3; // ∂U/∂ûᵢ
  gj: Vector3; // ∂U/∂ûⱼ
}

export const newGbPairResult = (): GbPairResult => ({
  energy: 0,
  force: new Vector3(),
  gi: new Vector3(),
  gj: new Vector3(),
});

/** Is this atom a Gay-Berne rod (a bead with an aspect ratio above one)? */
export const isRod = (a: Atom): boolean => !!a.bead && (a.bead.aspect ?? 1) > 1;

/**
 * Where a ray first meets a rod's drawn ellipsoid (semi-axes σ₀/2, σ₀/2, κσ₀/2
 * along û), as the distance along the unit `direction`, or null if it misses or
 * the rod is behind the origin.
 */
export const rayHitsRod = (origin: Vector3, direction: Vector3, a: Atom): number | null => {
  const u = a.orientation;
  if (!u) return null;
  const s0 = a.bead?.sigma ?? a.sigma;
  const r2 = 1 / ((s0 / 2) * (s0 / 2)); // 1 / (transverse semi-axis)²
  const c2 = 1 / (((a.bead?.aspect ?? 1) * s0) / 2) ** 2; // 1 / (long semi-axis)²
  const ox = origin.x - a.position.x;
  const oy = origin.y - a.position.y;
  const oz = origin.z - a.position.z;
  const oU = ox * u.x + oy * u.y + oz * u.z;
  const dU = direction.x * u.x + direction.y * u.y + direction.z * u.z;
  const oo = ox * ox + oy * oy + oz * oz;
  const od = ox * direction.x + oy * direction.y + oz * direction.z;
  const dd = direction.lengthSq();
  // (|p|² − (p·û)²) r2 + (p·û)² c2 = 1 along p = o + t d
  const A = (dd - dU * dU) * r2 + dU * dU * c2;
  const B = 2 * ((od - oU * dU) * r2 + oU * dU * c2);
  const C = (oo - oU * oU) * r2 + oU * oU * c2 - 1;
  const disc = B * B - 4 * A * C;
  if (disc < 0 || A === 0) return null;
  const t = (-B - Math.sqrt(disc)) / (2 * A);
  return t >= 0 ? t : null;
};

/** The rod's moment of inertia about a transverse axis, g/mol·Å². */
export const rodInertia = (a: Atom): number => {
  const k = a.bead?.aspect ?? 1;
  const s = a.bead?.sigma ?? a.sigma;
  return (a.mass * s * s * (k * k + 1)) / 20;
};

/**
 * Energy, force on i and the orientation gradients of one Gay-Berne pair.
 * Returns false (nothing written) beyond the cutoff.
 */
export const gbPair = (
  rij: Vector3,
  ui: Vector3,
  uj: Vector3,
  p: GbPairParams,
  out: GbPairResult,
  scratch = GB_SCRATCH,
): boolean => {
  const r = rij.length();
  if (r <= 0) return false;
  const rh = scratch.rh.copy(rij).multiplyScalar(1 / r);
  const a = rh.dot(ui);
  const b = rh.dot(uj);
  const c = ui.dot(uj);
  const k2 = p.kappa * p.kappa;
  const likeChi = (k2 - 1) / (k2 + 1);
  const xi = p.xi ?? likeChi;
  const xj = p.xj ?? likeChi;
  const chi2 = xi * xj;
  const plus = a + b;
  const minus = a - b;
  const D = 1 - chi2 * c * c;
  const N = xi * a * a + xj * b * b - 2 * chi2 * a * b * c;
  const S = N / D;
  const sig = p.sigma0 / Math.sqrt(1 - S);
  const R = (r - sig + p.sigma0) / p.sigma0;
  if (R >= GB_CUTOFF_R) return false;
  const e1 = 1 / Math.sqrt(D);
  const dp1 = 1 + CHI_PRIME * c;
  const dp2 = 1 - CHI_PRIME * c;
  const Sp = (plus * plus) / dp1 + (minus * minus) / dp2;
  const e2 = 1 - (CHI_PRIME / 2) * Sp;
  // ε₁^ν ε₂^μ written out for ν = 1, μ = 2 (Math.pow is slow in the hot loop).
  const e1n = e1;
  const e2m = e2 * e2;
  const eps = p.epsilon0 * e1n * e2m;
  // R below ~0.2 would overflow R⁻¹²; the soft wall of a rod is steep enough
  // that an integrator never gets there, but a user drag can. Clamp as the
  // pair kernel does: continue linearly below R = 0.5.
  const Rs = Math.max(R, 0.5);
  const Rinv = 1 / Rs;
  const R2 = Rinv * Rinv;
  const R6 = R2 * R2 * R2;
  const R12 = R6 * R6;
  let P = R12 - R6 - SHIFT;
  const dP = (-12 * R12 + 6 * R6) * Rinv; // dP/dR
  if (R < 0.5) {
    P += dP * (R - 0.5); // linear continuation, same slope
  }
  const U = 4 * eps * P;
  const dU_dr = (4 * eps * dP) / p.sigma0;
  const dU_dsig = -dU_dr;
  const dU_de2 = 4 * p.epsilon0 * e1n * 2 * e2 * P;
  const dU_de1 = 4 * p.epsilon0 * e2m * P;
  const dsig_dS = (sig * sig * sig) / (2 * p.sigma0 * p.sigma0);
  const dS_da = (2 * xi * a - 2 * chi2 * b * c) / D;
  const dS_db = (2 * xj * b - 2 * chi2 * a * c) / D;
  const dS_dc = (-2 * chi2 * a * b) / D + (2 * chi2 * c * N) / (D * D);
  const dSp_da = (2 * plus) / dp1 + (2 * minus) / dp2;
  const dSp_db = (2 * plus) / dp1 - (2 * minus) / dp2;
  const dSp_dc = (-CHI_PRIME * plus * plus) / (dp1 * dp1) + (CHI_PRIME * minus * minus) / (dp2 * dp2);
  const de2_dSp = -CHI_PRIME / 2;
  const de1_dc = chi2 * c * e1 * e1 * e1;
  const dU_da = dU_dsig * dsig_dS * dS_da + dU_de2 * de2_dSp * dSp_da;
  const dU_db = dU_dsig * dsig_dS * dS_db + dU_de2 * de2_dSp * dSp_db;
  const dU_dc = dU_dsig * dsig_dS * dS_dc + dU_de2 * de2_dSp * dSp_dc + dU_de1 * de1_dc;
  // ∇_r U = dU/dr r̂ + dU/da (ûᵢ − a r̂)/r + dU/db (ûⱼ − b r̂)/r ; force on i = −∇_r U.
  const f = out.force;
  f.copy(rh).multiplyScalar(dU_dr - (dU_da * a + dU_db * b) / r);
  f.addScaledVector(ui, dU_da / r);
  f.addScaledVector(uj, dU_db / r);
  f.multiplyScalar(-1);
  out.gi.copy(rh).multiplyScalar(dU_da).addScaledVector(uj, dU_dc);
  out.gj.copy(rh).multiplyScalar(dU_db).addScaledVector(ui, dU_dc);
  out.energy = U;
  return true;
};

const GB_SCRATCH = { rh: new Vector3() };

/** ⟨u_x²⟩, ⟨u_y²⟩, ⟨u_z²⟩ of a set of unit vectors. */
export const meanSquareOf = (us: readonly Vector3[]): { x: number; y: number; z: number } => {
  let x = 0;
  let y = 0;
  let z = 0;
  for (const u of us) {
    x += u.x * u.x;
    y += u.y * u.y;
    z += u.z * u.z;
  }
  const n = Math.max(1, us.length);
  return { x: x / n, y: y / n, z: z / n };
};

/** The dichroic ratio A∥ / A⊥ of a dye with order parameter S: (1 + 2S) / (1 − S). */
export const dichroicRatio = (S: number): number => (S < 1 ? (1 + 2 * S) / (1 - S) : Infinity);

/** The nematic order parameter S and director of a set of unit vectors (Q-tensor). */
export const nematicOrderOf = (us: readonly Vector3[]): { S: number; director: Vector3 } => {
  const n = us.length;
  if (n === 0) return { S: 0, director: new Vector3(0, 0, 1) };
  const q = new Float64Array(9);
  for (const u of us) {
    const c = [u.x, u.y, u.z];
    for (let i = 0; i < 3; i++)
      for (let j = 0; j < 3; j++) q[i * 3 + j] += (1.5 * c[i] * c[j] - (i === j ? 0.5 : 0)) / n;
  }
  const { values, vectors } = symmetricEigenDecompositionFlat(q, 3);
  let best = 0;
  for (let k = 1; k < 3; k++) if (values[k] > values[best]) best = k;
  const v = vectors[best];
  const director = new Vector3(v[0], v[1], v[2]).normalize();
  return { S: values[best], director };
};

export class GayBerne {
  /** Atom indices of the rods. */
  readonly rods: number[];
  private readonly inertia: Float64Array;
  private readonly alpha: Float64Array; // rad/fs², 3 per rod
  private readonly torque: Float64Array; // eV, 3 per rod
  /** The pair + field energy of the last compute, eV. */
  energy = 0;
  /** Σ rᵢⱼ·fᵢⱼ over the rod pairs of the last compute (amu·Å²/fs², the engine's virial units). */
  virialPair = 0;
  /** Reduced aligning field E* along fieldDirection: U = −½ ε₀ E*² (û·ê)² per rod. */
  fieldStrength = 0;
  readonly fieldDirection = new Vector3(0, 0, 1);
  /**
   * Periodic cell lengths (Å). When set, pair separations use the minimum
   * image, so the rods see a bulk with no walls. Positions stay unwrapped.
   * Null (the default) is the walled box. The first step of the molecular
   * lab's periodic mode; the probe that motivated it is recorded in memory.
   */
  periodic: { lx: number; ly: number; lz: number } | null = null;
  // Per kind-of-rod pair (kinds by width and length): the squared reach beyond which
  // no orientation brings the pair inside the cutoff, and the same plus the skin.
  private readonly kindOf: Int32Array;
  private kinds = 0;
  // Each kind's width, aspect ratio and well, to notice an edit of a rod's bead.
  private kindSigma: number[] = [];
  private kindAspect: number[] = [];
  private kindEpsilon: number[] = [];
  // The combined parameters of each kind pair (xi, xj always set).
  private kindParams: GbPairParams[] = [];
  private rangeSq = new Float64Array(0);
  private listRangeSq = new Float64Array(0);
  /** Rod slot by atom index. */
  private readonly slotOf = new Map<number, number>();
  private readonly rij = new Vector3();
  private readonly pair = newGbPairResult();
  private readonly tmp = new Vector3();
  // The pair list: entries (p, q, nx, ny, nz), p <= q, one per periodic image (q
  // shifted nx, ny, nz edges) within rangeSq + skin at the reference positions. The reach holds for any orientation, so a rod's turning
  // never invalidates it; only moves and a change of cell do, and validPairList
  // looks at both every step, whoever made them (a drag, a sync, the barostat).
  private pairList = new Int32Array(0);
  private pairCount = 0;
  private readonly refPositions: Float64Array;
  private refCell: { lx: number; ly: number; lz: number } | null = null;
  private pairListBuilt = false;
  // Each rod's wraps back into the cell since the list was built, in edges per axis.
  private readonly wrap: Int32Array;
  // The most edges any entry's image lies away (at least 1).
  private maxShift = 1;

  /** The rods of an atom list, or null when it has none. */
  static forAtoms(atoms: Atom[]): GayBerne | null {
    const rods: number[] = [];
    atoms.forEach((a, i) => {
      if (isRod(a)) rods.push(i);
    });
    return rods.length > 0 ? new GayBerne(atoms, rods) : null;
  }

  private constructor(atoms: Atom[], rods: number[]) {
    this.rods = rods;
    this.inertia = new Float64Array(rods.length);
    this.alpha = new Float64Array(3 * rods.length);
    this.torque = new Float64Array(3 * rods.length);
    this.refPositions = new Float64Array(3 * rods.length);
    this.wrap = new Int32Array(3 * rods.length);
    this.kindOf = new Int32Array(rods.length);
    rods.forEach((ai, k) => {
      this.slotOf.set(ai, k);
      this.inertia[k] = rodInertia(atoms[ai]);
    });
    this.refreshKinds(atoms);
  }

  // Group the rods into kinds (same width, aspect ratio and well) and set each kind
  // pair's parameters and reach. σ = σ₀/√(1 − S) is the Gaussian-overlap contact, at
  // most √((Lᵢ² + Lⱼ²)/2) (two rods end to end); beyond that + (R_c − 1) σ₀ no
  // orientation comes inside the cutoff. So a pair of short rods keeps a short reach
  // beside long ones.
  private refreshKinds(atoms: Atom[]): void {
    const kindKey = new Map<string, number>();
    const sig: number[] = [];
    const asp: number[] = [];
    const eps: number[] = [];
    this.rods.forEach((ai, k) => {
      const a = atoms[ai];
      const s0 = a.bead?.sigma ?? a.sigma;
      const kappa = a.bead?.aspect ?? 1;
      const e0 = a.bead?.epsilon ?? a.epsilon;
      const key = s0 + ':' + kappa + ':' + e0;
      let kind = kindKey.get(key);
      if (kind === undefined) {
        kind = sig.length;
        kindKey.set(key, kind);
        sig.push(s0);
        asp.push(kappa);
        eps.push(e0);
      }
      this.kindOf[k] = kind;
    });
    const nk = sig.length;
    this.kinds = nk;
    this.kindSigma = sig;
    this.kindAspect = asp;
    this.kindEpsilon = eps;
    this.kindParams = [];
    this.rangeSq = new Float64Array(nk * nk);
    this.listRangeSq = new Float64Array(nk * nk);
    for (let i = 0; i < nk; i++)
      for (let j = 0; j < nk; j++) {
        const pij = gbUnlikeParams(sig[i], eps[i], asp[i], sig[j], eps[j], asp[j]);
        this.kindParams.push(pij);
        const li = asp[i] * sig[i];
        const lj = asp[j] * sig[j];
        const sigmaMax = i === j ? li : Math.sqrt((li * li + lj * lj) / 2);
        const reach = Math.max(sigmaMax, pij.sigma0) + (GB_CUTOFF_R - 1) * pij.sigma0;
        this.rangeSq[i * nk + j] = reach * reach;
        this.listRangeSq[i * nk + j] = (reach + PAIR_LIST_SKIN) * (reach + PAIR_LIST_SKIN);
      }
    this.pairListBuilt = false;
  }

  // Has any rod's bead changed since the kinds were made (an edit of its width, length
  // or well)? Then the kinds, and the pair list with their reaches, are rebuilt.
  private kindsStale(atoms: Atom[]): boolean {
    const kindOf = this.kindOf;
    for (let p = 0; p < this.rods.length; p++) {
      const a = atoms[this.rods[p]];
      const k = kindOf[p];
      if ((a.bead?.sigma ?? a.sigma) !== this.kindSigma[k]) return true;
      if ((a.bead?.aspect ?? 1) !== this.kindAspect[k]) return true;
      if ((a.bead?.epsilon ?? a.epsilon) !== this.kindEpsilon[k]) return true;
    }
    return false;
  }

  // Does the list still hold every pair inside its reach? A pair's separation can have
  // changed by at most twice the largest move plus the change of the cell, the latter
  // once per edge an entry's image lies away. A wrap back into the cell is not a move:
  // it is counted in wrap, and compute adds it to the entries' image shifts.
  private validPairList(atoms: Atom[]): boolean {
    if (!this.pairListBuilt) return false;
    const pbc = this.periodic;
    const ref = this.refCell;
    if (!pbc !== !ref) return false;
    let used =
      pbc && ref
        ? this.maxShift * (Math.abs(pbc.lx - ref.lx) + Math.abs(pbc.ly - ref.ly) + Math.abs(pbc.lz - ref.lz))
        : 0;
    let maxSq = 0;
    const r0 = this.refPositions;
    const wrap = this.wrap;
    for (let p = 0; p < this.rods.length; p++) {
      const q = atoms[this.rods[p]].position;
      let dx = q.x - r0[3 * p];
      let dy = q.y - r0[3 * p + 1];
      let dz = q.z - r0[3 * p + 2];
      if (pbc) {
        const wx = Math.round(dx / pbc.lx);
        const wy = Math.round(dy / pbc.ly);
        const wz = Math.round(dz / pbc.lz);
        dx -= pbc.lx * wx;
        dy -= pbc.ly * wy;
        dz -= pbc.lz * wz;
        wrap[3 * p] = wx;
        wrap[3 * p + 1] = wy;
        wrap[3 * p + 2] = wz;
      }
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 > maxSq) maxSq = d2;
    }
    used += 2 * Math.sqrt(maxSq);
    return used < PAIR_LIST_SKIN;
  }

  private buildPairList(atoms: Atom[]): void {
    const rods = this.rods;
    const n = rods.length;
    const pbc = this.periodic;
    const r0 = this.refPositions;
    for (let p = 0; p < n; p++) {
      const q = atoms[rods[p]].position;
      r0[3 * p] = q.x;
      r0[3 * p + 1] = q.y;
      r0[3 * p + 2] = q.z;
    }
    this.wrap.fill(0);
    let count = 0;
    let maxShift = 1;
    let list = this.pairList;
    const kindOf = this.kindOf;
    const nk = this.kinds;
    const listRangeSq = this.listRangeSq;
    const push = (p: number, q: number, nx: number, ny: number, nz: number) => {
      if (5 * count + 5 > list.length) {
        const grown = new Int32Array(Math.max(5120, list.length * 2));
        grown.set(list);
        list = grown;
      }
      list[5 * count] = p;
      list[5 * count + 1] = q;
      list[5 * count + 2] = nx;
      list[5 * count + 3] = ny;
      list[5 * count + 4] = nz;
      count++;
      maxShift = Math.max(maxShift, Math.abs(nx), Math.abs(ny), Math.abs(nz));
    };
    for (let p = 0; p < n; p++) {
      const row = kindOf[p] * nk;
      // q = p: a rod and its own images, when it reaches past an edge (half the
      // offsets, so each image pair once).
      for (let q = pbc ? p : p + 1; q < n; q++) {
        const r2 = listRangeSq[row + kindOf[q]];
        const dx = r0[3 * p] - r0[3 * q];
        const dy = r0[3 * p + 1] - r0[3 * q + 1];
        const dz = r0[3 * p + 2] - r0[3 * q + 2];
        if (!pbc) {
          if (dx * dx + dy * dy + dz * dz < r2) push(p, q, 0, 0, 0);
          continue;
        }
        // Every image within reach, not only the nearest: a rod longer than half the
        // cell can reach two images of another rod, or images of itself.
        const r = Math.sqrt(r2);
        const x1 = Math.floor((r - dx) / pbc.lx);
        const y0 = Math.ceil((-r - dy) / pbc.ly);
        const y1 = Math.floor((r - dy) / pbc.ly);
        const z0 = Math.ceil((-r - dz) / pbc.lz);
        const z1 = Math.floor((r - dz) / pbc.lz);
        for (let ix = Math.ceil((-r - dx) / pbc.lx); ix <= x1; ix++) {
          const x = dx + ix * pbc.lx;
          for (let iy = y0; iy <= y1; iy++) {
            const y = dy + iy * pbc.ly;
            for (let iz = z0; iz <= z1; iz++) {
              const z = dz + iz * pbc.lz;
              if (x * x + y * y + z * z >= r2) continue;
              if (p === q && (ix < 0 || (ix === 0 && (iy < 0 || (iy === 0 && iz <= 0))))) continue;
              push(p, q, ix, iy, iz);
            }
          }
        }
      }
    }
    this.pairList = list;
    this.pairCount = count;
    this.maxShift = maxShift;
    this.refCell = pbc ? { lx: pbc.lx, ly: pbc.ly, lz: pbc.lz } : null;
    this.pairListBuilt = true;
  }

  /**
   * Add the rods' pair and field forces (÷ mass) to Atom.force, set their
   * angular accelerations from the torques, and return the energy (eV).
   */
  compute(atoms: Atom[]): number {
    const rods = this.rods;
    const n = rods.length;
    this.torque.fill(0);
    let energy = 0;
    const rij = this.rij;
    const out = this.pair;
    const pbc = this.periodic;
    let virial = 0;
    if (this.kindsStale(atoms)) this.refreshKinds(atoms);
    if (!this.validPairList(atoms)) this.buildPairList(atoms);
    const list = this.pairList;
    const kindParams = this.kindParams;
    const tail = GB_CUTOFF_R - 1;
    const wrap = this.wrap;
    const rangeSq = this.rangeSq;
    const kindOf = this.kindOf;
    const nk = this.kinds;
    for (let k = 0; k < this.pairCount; k++) {
      const p = list[5 * k];
      const q = list[5 * k + 1];
      const ai = atoms[rods[p]];
      const aj = atoms[rods[q]];
      const ui = ai.orientation;
      const uj = aj.orientation;
      if (!ui || !uj) continue;
      rij.subVectors(ai.position, aj.position);
      if (pbc) {
        // The entry's image, corrected for the wraps of both rods since the list was built.
        rij.x += (list[5 * k + 2] - wrap[3 * p] + wrap[3 * q]) * pbc.lx;
        rij.y += (list[5 * k + 3] - wrap[3 * p + 1] + wrap[3 * q + 1]) * pbc.ly;
        rij.z += (list[5 * k + 4] - wrap[3 * p + 2] + wrap[3 * q + 2]) * pbc.lz;
      }
      const kk = kindOf[p] * nk + kindOf[q];
      const r2 = rij.lengthSq();
      if (r2 > rangeSq[kk]) continue;
      const pp = kindParams[kk];
      // Most pairs within reach are still beyond the cutoff for their orientations:
      // out when σ ≤ r − (R_c − 1)σ₀, i.e. σ₀² ≤ t²(1 − S), the test gbPair makes,
      // here without its normalizing and square roots of S.
      const t = Math.sqrt(r2) - tail * pp.sigma0;
      if (t > pp.sigma0) {
        const xi = pp.xi!;
        const xj = pp.xj!;
        const a = rij.x * ui.x + rij.y * ui.y + rij.z * ui.z;
        const b = rij.x * uj.x + rij.y * uj.y + rij.z * uj.z;
        const c = ui.x * uj.x + ui.y * uj.y + ui.z * uj.z;
        const chi2 = xi * xj;
        const S = (xi * a * a + xj * b * b - 2 * chi2 * a * b * c) / (r2 * (1 - chi2 * c * c));
        if (pp.sigma0 * pp.sigma0 <= t * t * (1 - S)) continue;
      }
      if (!gbPair(rij, ui, uj, pp, out)) continue;
      energy += out.energy;
      // out.force is the force on i in eV/Å; the pair virial takes the
      // mass-free force in the engine's units (GF_CONVERSION_CONSTANT). For a rod
      // and its own image the two forces cancel; the torques add.
      if (!ai.fixed && !aj.fixed) virial += rij.dot(out.force) * GF_CONVERSION_CONSTANT;
      const fi = ai.force;
      const fj = aj.force;
      if (fi && !ai.fixed) fi.addScaledVector(out.force, GF_CONVERSION_CONSTANT / ai.mass);
      if (fj && !aj.fixed) fj.addScaledVector(out.force, -GF_CONVERSION_CONSTANT / aj.mass);
      // τ = −û × ∂U/∂û
      this.addTorque(p, ui, out.gi);
      this.addTorque(q, uj, out.gj);
    }
    // The aligning field: U_f = −½ c ε₀ E*² (û·ê)², ∂U_f/∂û = −c ε₀ E*² (û·ê) ê, with c the
    // rod's fieldCoupling (its dielectric anisotropy relative to the host; 1 by default).
    if (this.fieldStrength !== 0) {
      for (let p = 0; p < n; p++) {
        const ai = atoms[rods[p]];
        const ui = ai.orientation;
        if (!ui) continue;
        const e0 = ai.bead?.epsilon ?? ai.epsilon;
        const k = this.fieldStrength * this.fieldStrength * e0 * (ai.bead?.fieldCoupling ?? 1);
        if (k === 0) continue;
        const ue = ui.dot(this.fieldDirection);
        energy -= 0.5 * k * ue * ue;
        this.tmp.copy(this.fieldDirection).multiplyScalar(-k * ue);
        this.addTorque(p, ui, this.tmp);
      }
    }
    for (let p = 0; p < n; p++) {
      const c = GF_CONVERSION_CONSTANT / this.inertia[p];
      this.alpha[3 * p] = this.torque[3 * p] * c;
      this.alpha[3 * p + 1] = this.torque[3 * p + 1] * c;
      this.alpha[3 * p + 2] = this.torque[3 * p + 2] * c;
    }
    this.energy = energy;
    this.virialPair = virial;
    return energy;
  }

  private addTorque(p: number, u: Vector3, g: Vector3): void {
    // −(u × g)
    this.torque[3 * p] -= u.y * g.z - u.z * g.y;
    this.torque[3 * p + 1] -= u.z * g.x - u.x * g.z;
    this.torque[3 * p + 2] -= u.x * g.y - u.y * g.x;
  }

  /** The torque on rod k of the last compute, eV (for tests and readouts). */
  torqueOf(k: number): Vector3 {
    return new Vector3(this.torque[3 * k], this.torque[3 * k + 1], this.torque[3 * k + 2]);
  }

  /** Orientation predictor, the rotational twin of Atom.predict. */
  predict(atoms: Atom[], dt: number, dt2: number): void {
    const w = this.tmp;
    for (let p = 0; p < this.rods.length; p++) {
      const a = atoms[this.rods[p]];
      const u = a.orientation;
      const om = a.angularVelocity;
      if (!u || !om || a.fixed) continue;
      const ax = this.alpha[3 * p];
      const ay = this.alpha[3 * p + 1];
      const az = this.alpha[3 * p + 2];
      // û += (ω × û) dt + (α × û) dt²/2
      w.set(om.x * dt + ax * dt2, om.y * dt + ay * dt2, om.z * dt + az * dt2);
      const dx = w.y * u.z - w.z * u.y;
      const dy = w.z * u.x - w.x * u.z;
      const dz = w.x * u.y - w.y * u.x;
      u.set(u.x + dx, u.y + dy, u.z + dz).normalize();
      om.x += ax * dt;
      om.y += ay * dt;
      om.z += az * dt;
      om.addScaledVector(u, -om.dot(u));
    }
  }

  /** Angular-velocity corrector, the rotational twin of Atom.correct: ω += (α_new − α_old) h. */
  correct(atoms: Atom[], h: number, alphaOld: Float64Array): void {
    for (let p = 0; p < this.rods.length; p++) {
      const a = atoms[this.rods[p]];
      const om = a.angularVelocity;
      const u = a.orientation;
      if (!om || !u || a.fixed) continue;
      om.x += h * (this.alpha[3 * p] - alphaOld[3 * p]);
      om.y += h * (this.alpha[3 * p + 1] - alphaOld[3 * p + 1]);
      om.z += h * (this.alpha[3 * p + 2] - alphaOld[3 * p + 2]);
      om.addScaledVector(u, -om.dot(u));
    }
  }

  /** A copy of the current angular accelerations (taken before the force step). */
  snapshotAlpha(): Float64Array {
    return this.alpha.slice();
  }

  /** Σ ½ I ω² over the movable rods, eV. */
  kineticEnergy(atoms: Atom[]): number {
    let ke = 0;
    for (let p = 0; p < this.rods.length; p++) {
      const a = atoms[this.rods[p]];
      if (a.fixed || !a.angularVelocity) continue;
      ke += 0.5 * this.inertia[p] * a.angularVelocity.lengthSq();
    }
    return ke * EV_CONVERTER;
  }

  /** Rotational degrees of freedom: two per movable rod. */
  dof(atoms: Atom[]): number {
    let n = 0;
    for (const i of this.rods) if (!atoms[i].fixed) n += 2;
    return n;
  }

  /** Maxwell–Boltzmann angular velocities at T, perpendicular to each rod. */
  assignAngularVelocities(atoms: Atom[], temperature: number): void {
    const t = Math.sqrt(Math.max(0, temperature)) * VT_CONVERSION_CONSTANT;
    for (let p = 0; p < this.rods.length; p++) {
      const a = atoms[this.rods[p]];
      const u = a.orientation;
      const om = a.angularVelocity;
      if (!u || !om || a.fixed) continue;
      const c = t / Math.sqrt(this.inertia[p]);
      om.set(
        c * ModelUtil.nextStandardNormal(),
        c * ModelUtil.nextStandardNormal(),
        c * ModelUtil.nextStandardNormal(),
      );
      om.addScaledVector(u, -om.dot(u));
    }
  }

  /** ½ I ω² of one atom (0 for a non-rod), eV. */
  kineticEnergyOfAtom(atoms: Atom[], atomIndex: number): number {
    const k = this.slotOf.get(atomIndex);
    const a = atoms[atomIndex];
    if (k === undefined || !a?.angularVelocity || a.fixed) return 0;
    return 0.5 * this.inertia[k] * a.angularVelocity.lengthSq() * EV_CONVERTER;
  }

  /**
   * The Langevin step on the rods' angular velocities, the twin of the
   * translational one: ω ← c₁ ω + (c₂/√I) ξ, kept ⊥ û. `only` limits it to a
   * thermostat group.
   */
  applyLangevin(atoms: Atom[], c1: number, c2base: number, only?: ReadonlySet<number>): void {
    for (let p = 0; p < this.rods.length; p++) {
      const i = this.rods[p];
      if (only && !only.has(i)) continue;
      const a = atoms[i];
      const om = a.angularVelocity;
      const u = a.orientation;
      if (!om || !u || a.fixed) continue;
      const c2 = c2base / Math.sqrt(this.inertia[p]);
      om.set(
        c1 * om.x + c2 * ModelUtil.nextStandardNormal(),
        c1 * om.y + c2 * ModelUtil.nextStandardNormal(),
        c1 * om.z + c2 * ModelUtil.nextStandardNormal(),
      );
      om.addScaledVector(u, -om.dot(u));
    }
  }

  rescale(atoms: Atom[], ratio: number): void {
    for (const i of this.rods) {
      const a = atoms[i];
      if (!a.fixed) a.angularVelocity?.multiplyScalar(ratio);
    }
  }

  /** The nematic order parameter and director of the rods right now. */
  nematicOrder(atoms: Atom[]): { S: number; director: Vector3 } {
    const us: Vector3[] = [];
    for (const i of this.rods) {
      const u = atoms[i].orientation;
      if (u) us.push(u);
    }
    return nematicOrderOf(us);
  }

  /**
   * Smectic (layer) order: how strongly the rods' centers bunch into layers
   * stacked along the director, τ = |⟨exp(2πi z/d)⟩| (1 for perfect layers, near 0
   * for a nematic or a liquid, whose centers fill the cell evenly; about 1/√N for
   * N rods at random). z is the center's coordinate along the cell axis nearest
   * the director, and d runs over the spacings that fit the cell a whole number of
   * times (L/m), since a periodic cell can only hold whole layers; the best m
   * wins. The layer spacing d is returned too: about one rod length in a smectic A.
   * Needs the periodic cell (null without it, where the walls make layers of
   * their own).
   */
  smecticOrder(
    atoms: Atom[],
    cell: { lx: number; ly: number; lz: number } | null = this.periodic,
  ): { tau: number; layerSpacingAng: number; layers: number; axis: 'x' | 'y' | 'z'; S: number } | null {
    if (!cell || this.rods.length === 0) return null;
    const { S, director: d } = this.nematicOrder(atoms);
    const ax =
      Math.abs(d.x) >= Math.abs(d.y) && Math.abs(d.x) >= Math.abs(d.z)
        ? 'x'
        : Math.abs(d.y) >= Math.abs(d.z)
          ? 'y'
          : 'z';
    const L = cell[ax === 'x' ? 'lx' : ax === 'y' ? 'ly' : 'lz'];
    // Up to layers half a rod width apart: far more than any smectic packs.
    let minWidth = Infinity;
    for (const i of this.rods) minWidth = Math.min(minWidth, atoms[i].bead?.sigma ?? atoms[i].sigma);
    const mMax = Math.max(1, Math.floor(L / Math.max(1, minWidth)));
    let best = 0;
    let bestM = 1;
    for (let m = 1; m <= mMax; m++) {
      const q = (2 * Math.PI * m) / L;
      let c = 0;
      let s = 0;
      for (const i of this.rods) {
        const z = atoms[i].position[ax];
        c += Math.cos(q * z);
        s += Math.sin(q * z);
      }
      const t = Math.hypot(c, s) / this.rods.length;
      if (t > best) {
        best = t;
        bestM = m;
      }
    }
    return { tau: best, layerSpacingAng: L / bestM, layers: bestM, axis: ax, S };
  }

  /**
   * The rods' centers counted in slices along the cell axis nearest the director
   * (the density profile a smectic shows as one peak per layer), wrapped into the
   * cell: `bins` slices from 0 to L, each a count of rods.
   */
  layerProfile(
    atoms: Atom[],
    bins = 60,
    cell: { lx: number; ly: number; lz: number } | null = this.periodic,
  ): { axis: 'x' | 'y' | 'z'; lengthAng: number; counts: number[] } | null {
    if (!cell || this.rods.length === 0) return null;
    const d = this.nematicOrder(atoms).director;
    const ax =
      Math.abs(d.x) >= Math.abs(d.y) && Math.abs(d.x) >= Math.abs(d.z)
        ? 'x'
        : Math.abs(d.y) >= Math.abs(d.z)
          ? 'y'
          : 'z';
    const L = cell[ax === 'x' ? 'lx' : ax === 'y' ? 'ly' : 'lz'];
    const counts = new Array(bins).fill(0);
    for (const i of this.rods) {
      const z = atoms[i].position[ax];
      const w = z - L * Math.floor(z / L + 0.5) + L / 2; // into [0, L)
      counts[Math.min(bins - 1, Math.max(0, Math.floor((w / L) * bins)))]++;
    }
    return { axis: ax, lengthAng: L, counts };
  }

  /**
   * The order of each rod species on its own (one entry per bead type, most
   * numerous first), each about its own director. Empty when the rods are all of
   * one kind: the whole-lab S already says it.
   */
  nematicOrderBySpecies(atoms: Atom[]): {
    name: string;
    count: number;
    lengthAng: number;
    widthAng: number;
    S: number;
    dye: boolean;
    // ⟨u_x²⟩, ⟨u_y²⟩, ⟨u_z²⟩: the share of a dye's absorption of light polarized along
    // each axis (1 when every rod lies along it, 1/3 for rods pointing every which way).
    meanSquare: { x: number; y: number; z: number };
  }[] {
    const groups = new Map<string, { us: Vector3[]; lengthAng: number; widthAng: number; dye: boolean }>();
    for (const i of this.rods) {
      const a = atoms[i];
      const u = a.orientation;
      if (!u) continue;
      const name = a.bead?.name ?? '';
      let g = groups.get(name);
      if (!g) {
        const w = a.bead?.sigma ?? a.sigma;
        g = { us: [], lengthAng: w * (a.bead?.aspect ?? 1), widthAng: w, dye: !!a.bead?.dye };
        groups.set(name, g);
      }
      g.us.push(u);
    }
    if (groups.size < 2) return [];
    return [...groups.entries()]
      .map(([name, g]) => ({
        name,
        count: g.us.length,
        lengthAng: g.lengthAng,
        widthAng: g.widthAng,
        S: nematicOrderOf(g.us).S,
        dye: g.dye,
        meanSquare: meanSquareOf(g.us),
      }))
      .sort((p, q) => q.count - p.count || q.lengthAng - p.lengthAng);
  }
}
